import { getApiClient } from '../../axios-config';

/**
 * Project Documents API Service
 *
 * Thin wrapper over the FieldTime-owned Project Documents API
 * (routes/ai/project-docs/documents-route.js). Document-mutating calls
 * (upload/review/retry) each require a specific discipline, since every
 * document always belongs to exactly one real discipline.
 */
class DocumentsAPI {
  /**
   * List every document for a project, across every discipline -- no
   * discipline filter is applied here at all (the backend's own
   * ?disciplineId query param is optional and simply omitted). The
   * DocumentsPane groups the result by type then by discipline client-side
   * (see document-type-groups.js) using each row's own discipline_id/
   * discipline_name.
   * @param {string} projectId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of document objects (see
   *   documents-db.js::listProjectDocuments for the exact row shape,
   *   including the current version's processing_status).
   */
  static async listProjectDocuments(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/ai/project-docs/projects/${projectId}/documents`
    );
    return response.data.data.documents || [];
  }

  /**
   * Upload the first version of a new manual-upload document.
   * @param {string} projectId
   * @param {Object} params
   * @param {string} params.disciplineId
   * @param {string} params.documentTypeId
   * @param {string} params.title
   * @param {File} params.file
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created document object
   */
  static async uploadDocument(
    projectId,
    { disciplineId, documentTypeId, title, file },
    token
  ) {
    const apiClient = getApiClient(token);
    const formData = new FormData();
    formData.append('disciplineId', disciplineId);
    formData.append('documentTypeId', documentTypeId);
    formData.append('title', title);
    formData.append('file', file);
    const response = await apiClient.post(
      `/api/ai/project-docs/projects/${projectId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data.document;
  }

  /**
   * The retained FieldTime version history of one document, newest first
   * (at most the three newest versions -- ACC remains the long-term version
   * record).
   * @param {string} projectId
   * @param {string} documentId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array<{id, documentId, versionNumber, fileName,
   *   isCurrentVersion, createdAt, supersededAt}>>}
   */
  static async listDocumentVersions(projectId, documentId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/ai/project-docs/projects/${projectId}/documents/${documentId}/versions`
    );
    return response.data.data.versions || [];
  }

  /**
   * Upload a new version of an existing manual-upload document; it becomes
   * the current version.
   * @param {string} projectId
   * @param {string} documentId
   * @param {File} file
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} the created version
   */
  static async uploadDocumentVersion(projectId, documentId, file, token) {
    const apiClient = getApiClient(token);
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `/api/ai/project-docs/projects/${projectId}/documents/${documentId}/versions`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data.version;
  }

  /**
   * Text comparison of two retained versions of one document, base ->
   * target. Only the two version ids are sent; FieldTime resolves them
   * server-side.
   * @param {string} projectId
   * @param {string} documentId
   * @param {string} baseVersionId
   * @param {string} targetVersionId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{base, target, identicalContent, segments, truncated}>}
   */
  static async compareDocumentVersions(
    projectId,
    documentId,
    baseVersionId,
    targetVersionId,
    token
  ) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/ai/project-docs/projects/${projectId}/documents/${documentId}/versions/compare`,
      { baseVersionId, targetVersionId }
    );
    return response.data.data.comparison;
  }

  /**
   * Load the current Review Required decision (if any) for one document
   * version. Rejects (404, surfaced by the caller as "no review") when no
   * reviewable outcome has ever been recorded for this exact scope.
   * @param {string} projectId
   * @param {string} documentId
   * @param {string} documentVersionId
   * @param {string} disciplineId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} the safe review payload (id, findings,
   *   reviewStatus, reviewerUserId, decisionAt, reviewerNote, createdAt)
   */
  static async getDocumentReview(
    projectId,
    documentId,
    documentVersionId,
    disciplineId,
    token
  ) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/ai/project-docs/projects/${projectId}/documents/${documentId}/versions/${documentVersionId}/review`,
      { params: { disciplineId } }
    );
    return response.data.data.review;
  }

  /**
   * Approve a pending Review Required decision. On success, FieldTime has
   * already durably created a NEW ingestion attempt (dispatch) for the SAME
   * document version and begun delivering it -- no file re-upload occurs.
   * @param {string} projectId
   * @param {string} documentId
   * @param {string} documentVersionId
   * @param {string} reviewId
   * @param {string} disciplineId
   * @param {string} [reviewerNote]
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} the updated safe review payload
   */
  static async approveDocumentReview(
    projectId,
    documentId,
    documentVersionId,
    reviewId,
    disciplineId,
    reviewerNote,
    token
  ) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/ai/project-docs/projects/${projectId}/documents/${documentId}/versions/${documentVersionId}/review/${reviewId}/approve`,
      { disciplineId, reviewerNote }
    );
    return response.data.data.review;
  }

  /**
   * Reject a pending Review Required decision. Creates no new ingestion
   * attempt; the document remains unavailable to RAG/query.
   * @param {string} projectId
   * @param {string} documentId
   * @param {string} documentVersionId
   * @param {string} reviewId
   * @param {string} disciplineId
   * @param {string} [reviewerNote]
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} the updated safe review payload
   */
  static async rejectDocumentReview(
    projectId,
    documentId,
    documentVersionId,
    reviewId,
    disciplineId,
    reviewerNote,
    token
  ) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/ai/project-docs/projects/${projectId}/documents/${documentId}/versions/${documentVersionId}/review/${reviewId}/reject`,
      { disciplineId, reviewerNote }
    );
    return response.data.data.review;
  }

  /**
   * Retry processing for a document whose CURRENT version is stuck in a
   * terminal "error" status. On success, FieldTime has already durably
   * created a NEW ingestion dispatch for the SAME document version and
   * begun delivering it -- no file re-upload occurs, and no new document
   * version is created.
   * @param {string} projectId
   * @param {string} documentId
   * @param {string} documentVersionId
   * @param {string} disciplineId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{dispatchId: string}>}
   */
  static async retryDocumentProcessing(
    projectId,
    documentId,
    documentVersionId,
    disciplineId,
    token
  ) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/ai/project-docs/projects/${projectId}/documents/${documentId}/versions/${documentVersionId}/retry`,
      { disciplineId }
    );
    return response.data.data;
  }

  /**
   * Fetch one specific document version's file bytes, for clickable source
   * navigation (a chat citation, or a Piping/Ductwork Schedule row's own
   * provenance). Never a plain `<a href>` to an authenticated URL -- the
   * bearer token can only be attached via this authenticated axios call, so
   * the caller must open the returned Blob itself (e.g. via an object URL),
   * never navigate the browser directly to this endpoint's URL.
   * @param {string} projectId
   * @param {string} documentId
   * @param {string} documentVersionId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{blob: Blob, fileName: string}>}
   */
  static async getDocumentVersionFile(
    projectId,
    documentId,
    documentVersionId,
    token
  ) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/ai/project-docs/projects/${projectId}/documents/${documentId}/versions/${documentVersionId}/file`,
      { responseType: 'blob' }
    );
    const contentDisposition = response.headers['content-disposition'];
    const match =
      typeof contentDisposition === 'string' &&
      contentDisposition.match(/filename="?([^";]+)"?/);
    return { blob: response.data, fileName: match ? match[1] : 'document' };
  }
}

export default DocumentsAPI;
